import styles from './productos-page.module.scss';
import React from "react";

import ProductCard from "../../components/product-card/product-card";
import Hero from "../../components/hero/hero";
import { HERO_WAVE_PAGE } from "../../components/hero/waves";
import Paginator from "../../components/paginator/paginator";
import NotificationCarousel from "../../components/notification-carousel/notification-carousel";
import SearchFilter from "../../components/search-filter/search-filter";
import api, { Notification, Product } from "../../services/api";

export interface ProductosPageProps {
  name?: string;
  classes?: string;
}

/** Productos por página (2 filas de 3 en desktop). */
const PER_PAGE = 6;

interface State {
  products: Product[] | null;
  notifications: Notification[];
  loading: boolean;
  error?: string;
  /** Página actual (1-based). */
  page: number;
  /** Término de búsqueda actual (para el mensaje de "sin resultados"). */
  query: string;
}

/**
 * Productos: cabecera (hero) + descripción + buscador + listado del catálogo
 * (paginado) + avisos (carrusel) en el paginador sticky. Los datos vienen del
 * API público (`/api/products`, `/api/notifications`).
 */
export default class ProductosPage extends React.Component<ProductosPageProps, State> {
  static jsClass = "ProductosPage";
  static defaultProps: Partial<ProductosPageProps> = {
    name: "mbProductos",
  };

  state: State = {
    products: null,
    notifications: [],
    loading: true,
    page: 1,
    query: "",
  };

  componentDidMount() {
    this.loadProducts("");

    api
      .listNotifications("products")
      .then((notifications) => this.setState({ notifications }))
      .catch(() => this.setState({ notifications: [] }));
  }

  /** Carga el catálogo (con búsqueda `q` opcional) y vuelve a la página 1. */
  private loadProducts = (q = "") => {
    this.setState({ loading: true, query: q, page: 1 });
    api
      .listProducts(q)
      .then((products) => this.setState({ products, loading: false }))
      .catch((e) => this.setState({ error: String(e?.message ?? e), loading: false }));
  };

  render(): React.ReactNode {
    const { classes } = this.props;
    const { products, notifications, loading, error, page, query } = this.state;

    const totalPages = products ? Math.max(1, Math.ceil(products.length / PER_PAGE)) : 1;
    const current = Math.min(Math.max(page, 1), totalPages);
    const visible = products
      ? products.slice((current - 1) * PER_PAGE, current * PER_PAGE)
      : [];
    const showPaginator = !loading && !error && (totalPages > 1 || notifications.length > 0);

    return (
      <div className={styles['mb-home'] + (classes ? ' ' + classes : '')}>
        <Hero
          src="/assets/hero/counter.jpg"
          title="Productos Mangibone"
          tagline="Quesos artesanales con sabor a tradición."
          align="start"
          wave={HERO_WAVE_PAGE}
        />

        <section className={styles['mb-intro']}>
          <div className={styles['mb-wrap']}>
            <div className={styles['mb-intro__grid']}>
              <div className={styles['mb-intro__text']}>
                <h2 className={styles['mb-h2']}>Nuestros productos</h2>
                <p>
                  Queso artesanal de leche 100% de vaca, elaborado con dedicación
                  y tradición familiar en Los Pinos, Jalisco.
                </p>
                <p>
                  Descubre nuestra variedad de quesos frescos y fundidos, lácteos
                  y cárnicos, directo de nuestra cremería.
                </p>
              </div>
              <img
                className={styles['mb-intro__img']}
                src="/assets/productos/manchego.jpg"
                alt="Productos Mangibone"
              />
            </div>
          </div>
        </section>

        <SearchFilter
          placeholder="Buscar en el catálogo…"
          onSearch={this.loadProducts}
        />

        <section className={styles['mb-grid-section']}>
          <div className={styles['mb-wrap']}>
            {loading ? (
              <p className={styles['mb-state']}>Cargando productos…</p>
            ) : error ? (
              <p className={styles['mb-state']}>No se pudo cargar el catálogo ({error}).</p>
            ) : !products || products.length === 0 ? (
              <p className={styles['mb-state']}>
                {query
                  ? `No se encontraron productos para «${query}».`
                  : "Todavía no hay productos publicados."}
              </p>
            ) : (
              <div className={styles['mb-products']}>
                {visible.map((product) => (
                  <ProductCard key={product.slug} product={product} />
                ))}
              </div>
            )}
          </div>
        </section>

        {showPaginator && (
          <Paginator
            page={current}
            total={totalPages}
            onChange={(p) => this.setState({ page: p })}>
            <NotificationCarousel notifications={notifications} />
          </Paginator>
        )}
      </div>
    );
  }
}
